import type { ReactNode } from 'react';
import { DataRow, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { currencyFormat } from '~/utils/currencyFormat';

export type TAmountRowVariant = 'default' | 'credit' | 'total';

export interface IAmountRowProps {
  /** Row label. ReactNode because a few rows nest a data-cy span inside the text. */
  label: ReactNode;
  /**
   * The raw number — never a pre-formatted string. AmountRow owns formatting so
   * the detail rows cannot drift from the receipt tables again.
   */
  amount: number | null | undefined;
  /** 'credit' renders a leading minus; 'total' colours the label. @default 'default' */
  variant?: TAmountRowVariant;
  /** Trailing unit, e.g. '/hr'. */
  suffix?: string;
  /** Rendered in place of an amount when `amount` is null/undefined, e.g. '$--.--'. */
  emptyText?: string;
  /** Strikes the value through — cancelled and expired shifts. @default false */
  struck?: boolean;
  /** Element beside the label: a tooltip icon or a timezone badge. */
  icon?: ReactNode;
  /**
   * Bottom rule. Defaults to false so the fee rows stay borderless like the
   * pre-refactor UI; pass divider to opt a row back into the DS rule.
   */
  divider?: boolean;
  /** data-cy for the value node, for the Cypress selectors that target it directly. */
  valueDataCy?: string;
  'data-cy'?: string;
}

export const AmountRow = ({
  label,
  amount,
  variant = 'default',
  suffix,
  emptyText,
  struck = false,
  icon,
  divider = false,
  valueDataCy,
  'data-cy': dataCy,
}: IAmountRowProps) => {
  const isMissing = amount === null || amount === undefined;
  const sign = variant === 'credit' ? '-' : '';
  // Credits normalise a pre-negated amount; every other variant keeps the sign
  // the caller gave — a negative adjustment must not silently render positive.
  const magnitude = variant === 'credit' ? Math.abs(amount ?? 0) : (amount ?? 0);

  const formatted = isMissing && emptyText
    ? emptyText
    : `${sign}$${currencyFormat(magnitude)}${suffix ? ` ${suffix}` : ''}`;

  // <s> carries the strike semantically, so no inline style and no reliance on a
  // Tailwind utility that is absent from the precompiled DS stylesheet.
  const value = struck ? <s>{formatted}</s> : formatted;

  return (
    <DataRow
      data-cy={dataCy}
      divider={divider}
      icon={icon}
      label={
        variant === 'total' ? (
          <Typography as="span" bold color={colors.success[500]}>
            {label}
          </Typography>
        ) : (
          label
        )
      }
      value={
        <Typography as="span" data-cy={valueDataCy}>
          {value}
        </Typography>
      }
    />
  );
};
